import { useEffect, useState } from 'react';
import { api } from '../api';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  name: string;
  avatarUrl?: string | null;
  // When provided, the component fetches and caches a presigned R2 GET URL
  // for this user. Use either avatarUrl (when you already have one) or
  // userId (when you don't) — never both. If both are passed, avatarUrl
  // wins.
  userId?: string;
  size?: AvatarSize;
  /** A person on a card is a rounded square in this design; the account
   *  itself — a profile button, a row in a list — stays a circle. */
  shape?: 'circle' | 'square';
  className?: string;
}

// Pixel size + Tailwind class per size token. The text scales with the chip.
const SIZE_PX: Record<AvatarSize, number> = { xs: 24, sm: 32, md: 40, lg: 64, xl: 96 };
const TEXT_CLASS: Record<AvatarSize, string> = {
  xs: 'text-[10px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-xl',
  xl: 'text-3xl',
};

// Photographs render grayscale in this design, so the initials that stand in
// for a missing photo are quiet too: eight desaturated chips off the iOS grey
// family, not the warm gold set they replace. A person is still recognisable
// by their own colour without a chip shouting across the list.
const PALETTE: { bg: string; fg: string }[] = [
  { bg: '#F2F2F7', fg: '#1A1A1A' }, // secondary
  { bg: '#E8EAF0', fg: '#31394A' },
  { bg: '#EAF0F6', fg: '#2A4763' },
  { bg: '#EDEFEA', fg: '#3B4A34' },
  { bg: '#F1EDEA', fg: '#4A3B31' },
  { bg: '#EBEEF1', fg: '#38424D' },
  { bg: '#F0ECF2', fg: '#443552' },
  { bg: '#E9F0EE', fg: '#2D4A43' },
];

// Deterministic palette pick from the display name. We hash the string
// once — different staff names land on different chips, and the same name
// always lands on the same chip (so the staff list stays visually stable
// across reloads).
function pickPalette(name: string): { bg: string; fg: string } {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(h) % PALETTE.length]!;
}

// Extract up to 2 leading letters from the first 2 whitespace-separated
// words. Unicode-aware so Hebrew (מַ + רִ → מר), Arabic / Urdu (ع + ر),
// and Tagalog (Mary Joy → MJ) all work. We use Array.from to iterate by
// codepoint, not by UTF-16 unit, so surrogate-pair scripts don't lose
// their first glyph.
function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const words = trimmed.split(/\s+/).slice(0, 2);
  const letters: string[] = [];
  for (const w of words) {
    const first = Array.from(w)[0];
    if (first) letters.push(first.toLocaleUpperCase());
  }
  return letters.join('') || '?';
}

// localStorage cache for presigned avatar URLs, keyed by user_id. The R2
// presigned URL is valid for 1 hour; we expire our cache 10 minutes early
// so we never hand out a URL that's about to die.
const CACHE_KEY = 'fm.avatarUrls';
const CACHE_TTL_MS = 50 * 60 * 1000; // 50 min
const NEGATIVE_TTL_MS = 5 * 60 * 1000; // 5 min for "no avatar" so we don't refetch on every render
const memCache = new Map<string, { url: string | null; expiresAt: number }>();

type DiskCache = Record<string, { url: string | null; expiresAt: number }>;

function readDiskCache(): DiskCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as DiskCache) : {};
  } catch {
    return {};
  }
}

function writeDiskCache(cache: DiskCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Quota / private mode — fine, we still have the in-memory map.
  }
}

function rememberAvatar(userId: string, url: string | null): void {
  const ttl = url ? CACHE_TTL_MS : NEGATIVE_TTL_MS;
  const entry = { url, expiresAt: Date.now() + ttl };
  memCache.set(userId, entry);
  const disk = readDiskCache();
  disk[userId] = entry;
  writeDiskCache(disk);
}

function recallAvatar(userId: string): { url: string | null } | null {
  const now = Date.now();
  const mem = memCache.get(userId);
  if (mem && mem.expiresAt > now) return { url: mem.url };
  const disk = readDiskCache();
  const hit = disk[userId];
  if (hit && hit.expiresAt > now) {
    memCache.set(userId, hit);
    return { url: hit.url };
  }
  return null;
}

// Pub-sub for live updates. When the owner re-uploads Maria's photo the
// EditPhoto sheet calls invalidateAvatarCache(maria_id); every mounted
// <Avatar userId={maria_id}> instance gets notified and refetches without
// needing to be unmounted/remounted by the parent.
type Listener = () => void;
const listeners = new Map<string, Set<Listener>>();

function notify(userId: string): void {
  const set = listeners.get(userId);
  if (!set) return;
  for (const l of set) l();
}

function subscribe(userId: string, l: Listener): () => void {
  let set = listeners.get(userId);
  if (!set) {
    set = new Set();
    listeners.set(userId, set);
  }
  set.add(l);
  return () => {
    set?.delete(l);
    if (set && set.size === 0) listeners.delete(userId);
  };
}

/**
 * Invalidate the cached presigned URL for a user — call this after the
 * owner (or self) uploads a new avatar so every mounted <Avatar> instance
 * for that user re-fetches and the new photo appears live.
 */
export function invalidateAvatarCache(userId: string): void {
  memCache.delete(userId);
  const disk = readDiskCache();
  delete disk[userId];
  writeDiskCache(disk);
  notify(userId);
}

// In-flight de-duper. If twelve <Avatar userId="u_123" /> mount at once
// (e.g. an op list with 12 assignees, all the same staff member), we make
// one HTTP request, not twelve.
const inflight = new Map<string, Promise<string | null>>();

async function fetchAvatarUrl(userId: string): Promise<string | null> {
  const existing = inflight.get(userId);
  if (existing) return existing;
  const promise = (async () => {
    try {
      const r = await api<{ url: string | null }>(`/v1/uploads/avatar-url/${userId}`);
      rememberAvatar(userId, r.url);
      return r.url;
    } catch {
      // Avatar resolution is decorative — never throw to the surface.
      rememberAvatar(userId, null);
      return null;
    } finally {
      inflight.delete(userId);
    }
  })();
  inflight.set(userId, promise);
  return promise;
}

/**
 * Hook to resolve a user's avatar URL with localStorage caching. Returns
 * `undefined` while loading (so callers can render an initials chip
 * instantly without a flash) and `string | null` once resolved.
 */
export function useAvatarUrl(userId: string | null | undefined): string | null | undefined {
  const [url, setUrl] = useState<string | null | undefined>(() => {
    if (!userId) return null;
    const cached = recallAvatar(userId);
    return cached ? cached.url : undefined;
  });

  useEffect(() => {
    if (!userId) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    const resolve = (force = false) => {
      const cached = force ? null : recallAvatar(userId);
      if (cached) {
        setUrl(cached.url);
        return;
      }
      // Set to undefined to indicate "fetching" — but only if we have nothing
      // cached. If a prior fetch resolved, keep showing it while we refresh
      // in the background; otherwise we'd flash initials then photo.
      if (!cached && force) setUrl(undefined);
      void fetchAvatarUrl(userId).then((next) => {
        if (!cancelled) setUrl(next);
      });
    };
    resolve();
    const unsub = subscribe(userId, () => resolve(true));
    return () => {
      cancelled = true;
      unsub();
    };
  }, [userId]);

  return url;
}

export function Avatar({
  name,
  avatarUrl,
  userId,
  size = 'md',
  shape = 'circle',
  className = '',
}: AvatarProps) {
  // When the caller already has a URL (e.g. own /me row right after upload),
  // use it directly. Otherwise resolve via cached lookup using userId.
  // Passing avatarUrl skips the network — the hook still has to be called
  // unconditionally to satisfy the rules of hooks, so we pass null in that
  // case to short-circuit cleanly.
  const resolved = useAvatarUrl(avatarUrl !== undefined ? null : (userId ?? null));
  const url = avatarUrl !== undefined ? avatarUrl : (resolved ?? null);
  const px = SIZE_PX[size];
  const palette = pickPalette(name);
  const initials = initialsOf(name);

  // A photograph of a person on a card is a rounded square in this design and
  // a circle everywhere it stands for the account — the profile button, a row
  // in a list. Both shapes are his; the difference is what the picture means.
  const base = `inline-flex items-center justify-center overflow-hidden shrink-0 select-none ${
    shape === 'square' ? 'rounded-2xl' : 'rounded-full'
  }`;
  const style: React.CSSProperties = {
    width: px,
    height: px,
    backgroundColor: url ? '#f5f6f8' : palette.bg,
    color: palette.fg,
  };

  if (url) {
    return (
      <span className={`${base} ${className}`} style={style}>
        <img
          src={url}
          alt={name}
          width={px}
          height={px}
          className={`h-full w-full object-cover ${shape === 'square' ? 'grayscale' : ''}`}
          loading="lazy"
        />
      </span>
    );
  }

  return (
    <span
      className={`${base} ${TEXT_CLASS[size]} font-semibold ${className}`}
      style={style}
      aria-label={name}
    >
      {initials}
    </span>
  );
}
